"use client";

import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import { Check, Circle, Loader2 } from "lucide-react";
import { motion } from "motion/react";
import { cn } from "@/modern/utils";
import type { GeneratingUiMode, Persona } from "../context/AnalyzeFileContext";
import {
  ADVISOR_SCAN_STEPS,
  ANALYZE_FEED_ROW_BODY_CLASS,
  DEFAULT_SCAN_STEPS,
  SCAN_STEP_INTERVAL_MS,
} from "../analyzeConstants";
import { AdvisorAccentBar } from "./AdvisorAccentBar";

const InsightSkeletonLines = () => (
  <div className="mt-3 space-y-2 rounded-lg border border-dashed border-border/60 bg-background/40 p-3">
    <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Preview</p>
    {[100, 92, 78, 55].map((width, i) => (
      <motion.div
        key={width}
        className="h-2.5 rounded-full bg-muted"
        initial={{ opacity: 0.35 }}
        animate={{ opacity: [0.35, 0.85, 0.35] }}
        transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.12 }}
        style={{ width: `${width}%` }}
      />
    ))}
  </div>
);

const useScanStepHighlight = (
  personaId: string,
  stepCount: number,
  getGenerationStartedAt: (id: string) => number | undefined,
) => {
  const computeHighlight = useCallback(() => {
    const startedAt = getGenerationStartedAt(personaId);
    if (!startedAt || stepCount < 1) return 0;
    const elapsed = Date.now() - startedAt;
    return Math.min(stepCount - 1, Math.floor(elapsed / SCAN_STEP_INTERVAL_MS));
  }, [personaId, stepCount, getGenerationStartedAt]);

  const [highlightIndex, setHighlightIndex] = useState(computeHighlight);

  useEffect(() => {
    setHighlightIndex(computeHighlight());
    const id = window.setInterval(() => setHighlightIndex(computeHighlight()), 400);
    return () => window.clearInterval(id);
  }, [computeHighlight]);

  return highlightIndex;
};

const useSkipGeneratingEntrance = (
  personaId: string,
  getGenerationStartedAt: (personaId: string) => number | undefined,
) => {
  const startedAt = getGenerationStartedAt(personaId);
  return startedAt != null && Date.now() - startedAt > 400;
};

const GeneratingCardShell = ({
  persona,
  skipEntrance,
  hideAccentBar = false,
  children,
}: {
  persona: Persona;
  skipEntrance: boolean;
  hideAccentBar?: boolean;
  children: ReactNode;
}) => (
  <motion.div
    initial={skipEntrance ? false : { opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -6 }}
    className={cn(
      "relative rounded-lg bg-muted/30 overflow-hidden",
      !hideAccentBar && "flex gap-0",
    )}
    role="status"
    aria-live="polite"
    aria-busy="true"
  >
    {!hideAccentBar && <AdvisorAccentBar personaId={persona.id} />}
    <div className={cn(ANALYZE_FEED_ROW_BODY_CLASS, hideAccentBar && "px-2.5")}>{children}</div>
  </motion.div>
);

const CompactTypingIndicator = ({
  hidePersonaHeader,
}: {
  hidePersonaHeader?: boolean;
}) => (
  <>
    {!hidePersonaHeader && (
      <p className="text-sm font-medium text-foreground mb-2">Analyzing…</p>
    )}
    <div className="bg-muted rounded-lg px-4 py-3 inline-flex shadow-sm border border-border/50">
      <div className="flex items-center gap-1.5" aria-label="Generating insight">
        {[0, 150, 300].map((delay) => (
          <motion.span
            key={delay}
            className="block h-2.5 w-2.5 rounded-full bg-primary"
            animate={{ y: [0, -5, 0], opacity: [0.45, 1, 0.45] }}
            transition={{
              duration: 0.55,
              repeat: Infinity,
              ease: "easeInOut",
              delay: delay / 1000,
            }}
          />
        ))}
      </div>
    </div>
  </>
);

const AnalyzingActivityCard = ({
  persona,
  getGenerationStartedAt,
  hidePersonaHeader,
}: {
  persona: Persona;
  getGenerationStartedAt: (personaId: string) => number | undefined;
  hidePersonaHeader?: boolean;
}) => {
  const steps = ADVISOR_SCAN_STEPS[persona.id] ?? DEFAULT_SCAN_STEPS;
  const highlightIndex = useScanStepHighlight(persona.id, steps.length, getGenerationStartedAt);

  return (
    <>
      {!hidePersonaHeader && (
        <div className="flex items-center gap-2.5 mb-3 py-0.5">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">{persona.name}</p>
            <p className="text-xs text-foreground/75 line-clamp-2">{persona.description}</p>
          </div>
        </div>
      )}

      <ul className="space-y-1.5">
        {steps.map((step, index) => {
          const isDone = index < highlightIndex;
          const isActive = index === highlightIndex;
          return (
            <li
              key={step}
              className={cn(
                "flex items-start gap-2 rounded-md px-2 py-1.5 text-xs transition-colors",
                isActive && "bg-primary/8 text-foreground",
                isDone && "text-muted-foreground",
                !isDone && !isActive && "text-muted-foreground/70",
              )}
            >
              <span className="mt-0.5 shrink-0">
                {isDone ? (
                  <Check className="w-3.5 h-3.5 text-emerald-500" strokeWidth={2.5} />
                ) : isActive ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
                ) : (
                  <Circle className="w-3.5 h-3.5 text-muted-foreground/40" />
                )}
              </span>
              <span className={cn("leading-snug", isActive && "font-medium")}>{step}</span>
            </li>
          );
        })}
      </ul>

      <InsightSkeletonLines />

      <p className="mt-2.5 text-[10px] text-muted-foreground leading-snug">
        You can switch advisors or pop out this panel — this run continues in the background.
      </p>
    </>
  );
};

export const AnalyzeGeneratingIndicator = ({
  persona,
  mode,
  getGenerationStartedAt,
  hidePersonaHeader = false,
}: {
  persona: Persona;
  mode: GeneratingUiMode;
  getGenerationStartedAt: (personaId: string) => number | undefined;
  hidePersonaHeader?: boolean;
}) => {
  const skipEntrance = useSkipGeneratingEntrance(persona.id, getGenerationStartedAt);
  const body =
    mode === "compact" ? (
      <CompactTypingIndicator hidePersonaHeader={hidePersonaHeader} />
    ) : (
      <AnalyzingActivityCard
        persona={persona}
        getGenerationStartedAt={getGenerationStartedAt}
        hidePersonaHeader={hidePersonaHeader}
      />
    );

  return (
    <GeneratingCardShell
      persona={persona}
      skipEntrance={skipEntrance}
      hideAccentBar={hidePersonaHeader}
    >
      {body}
    </GeneratingCardShell>
  );
};
